import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
import { NeutralColors, SharedColors } from '@fluentui/theme';
import { Link, mergeStyles, mergeStyleSets, Persona, PersonaSize, Rating, RatingSize, Stack, Text, Icon } from '@fluentui/react';
import * as PropTypes from 'prop-types';
import { SpzaInstrumentService } from '@shared/services/telemetry/spza/spzaInstrument';
import { getWindow } from '@shared/services/window';
import { Constants } from '@shared/utils/constants';
import { LongContent } from '@shared/components/longContent';
import { getLocaleDate } from '@shared/utils/appUtils';
import { ReviewQAContent } from '@shared/components/reviewQAContent';
import { getProviderNameByReviewSource, launchTelemetry } from '@shared/utils/reviewsUtils';
import { logger } from '@src/logger';
import { AppReviewSocialContent } from '@shared/components/appReviewSocialContent';
import { noop } from 'lodash-es';
// 60px is the height of 3 lines.
const contentClipHeight = 60;
const contentStyles = mergeStyleSets({
    providerLogo: {
        height: 16,
        width: 16,
        marginRight: 10
    },
    reviewLinkToSource: {
        ':link': {
            color: SharedColors.cyanBlue10,
            textDecoration: 'underline'
        }
    },
    externalReviewProvider: {
        display: 'inline-flex',
        alignItems: 'center'
    },
    providedByWrapper: [
        'providedBy',
        'c-paragraph-3',
        {
            lineHeight: 14,
            fontSize: 10,
            color: NeutralColors.gray130,
            display: 'flex',
            width: '75%',
            borderTopStyle: 'solid',
            borderTopWidth: 0.5,
            borderTopColor: NeutralColors.gray30
        }
    ],
    reviewContent: [
        'reviewContent'
    ],
    reviewAndIsvReply: [
        'reviewAndIsvReply'
    ],
    reviewItem: [
        'reviewItem'
    ],
    authorDetailsContainer: [
        'authorName',
        'c-paragraph-3'
    ],
    authorPersona: [
        'spza-user-default-thumbnail-image',
        {
            marginBottom: 8
        }
    ],
    reviewRating: [
        'reviewRating'
    ],
    reviewTitle: [
        'c-heading-6',
        'contentHeader'
    ],
    reviewRightContent: [
        'rightContent',
        {
            overflow: 'hidden'
        }
    ],
    reviewDateWrapper: [
        'c-paragraph-3',
        {
            marginBottom: 4
        }
    ],
    dateLabel: {
        fontSize: 12,
        lineHeight: 16
    },
    editedLabel: {
        color: NeutralColors.gray130,
        overflow: 'hidden'
    },
    externalSummaryUrlImage: {
        height: 13,
        width: 13,
        color: SharedColors.cyanBlue10,
        marginTop: 1
    }
});
// Displays an individual app review
export const AppReviewItem = ({ authorPersona = false, appId, reviewItemShown = noop, review, locale, isEmbed, publisher, iconUrl, isSignedIn = false, isMarkedAsHelpful = false, onToggleMarkAsHelpful, openSignInModal, fallbackReviewSourceName, dateLabelClassName }, context)=>{
    var _review_review_content;
    const rootDivRef = useRef();
    const instrument = SpzaInstrumentService.getProvider();
    useEffect(()=>{
        if (review) {
            reviewItemShown(review.id, rootDivRef);
        }
    }, []);
    useEffect(()=>{
        if (review) {
            reviewItemShown(review.id, rootDivRef);
        }
    }, [
        review === null || review === void 0 ? void 0 : review.id
    ]);
    const showReportAbuseDialog = ()=>{
        const payload = {
            page: getWindow().location.href,
            action: Constants.Telemetry.Action.Click,
            actionModifier: Constants.Telemetry.ActionModifier.ReportCTA,
            appName: appId
        };
        instrument.probe('logAndFlushTelemetryInfo', payload);
        logger.info('', {
            action: payload.action,
            actionModifier: payload.actionModifier,
            appName: payload.appName
        });
        context.reportAbuseCallback(appId, review.id, review.title, review.source);
    };
    const getFullName = (review)=>{
        var _review_customer_info;
        let result = '';
        if (review.customer_info && review.customer_info.name) {
            result += review.customer_info.name;
        }
        if ((_review_customer_info = review.customer_info) === null || _review_customer_info === void 0 ? void 0 : _review_customer_info.is_anonymous) {
            result = '';
        }
        if (!result) {
            return context.loc('ReviewItem_AnonymousName');
        }
        return result;
    };
    const renderProviderSection = (isThirdPartyReview)=>{
        return /*#__PURE__*/ _jsx("div", {
            className: contentStyles.providedByWrapper,
            children: isThirdPartyReview ? /*#__PURE__*/ _jsx("div", {
                className: contentStyles.externalReviewProvider,
                children: /*#__PURE__*/ _jsx(Text, {
                    block: true,
                    variant: "xSmall",
                    children: /*#__PURE__*/ _jsxs(Stack, {
                        horizontal: true,
                        tokens: {
                            childrenGap: 4
                        },
                        children: [
                            /*#__PURE__*/ _jsxs(Link, {
                                className: contentStyles.reviewLinkToSource,
                                href: review.third_party_review_url,
                                target: "_blank",
                                onClick: ()=>{
                                    launchTelemetry(Constants.Telemetry.Action.Click, Constants.Telemetry.ActionModifier.ExternalReviewLink, {
                                        offerId: review.offer_id,
                                        source: review.source,
                                        reviewId: review.id
                                    });
                                    logger.info(JSON.stringify({
                                        offerId: review.offer_id,
                                        source: review.source,
                                        reviewId: review.id
                                    }), {
                                        action: Constants.Telemetry.Action.Click,
                                        actionModifier: Constants.Telemetry.ActionModifier.ExternalReviewLink
                                    });
                                },
                                children: [
                                    getProviderNameByReviewSource(review.source, fallbackReviewSourceName),
                                    ' ',
                                    context.loc('Global_Loc_review', 'review')
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Icon, {
                                iconName: "NavigateExternalInline",
                                className: contentStyles.externalSummaryUrlImage
                            })
                        ]
                    })
                })
            }) : /*#__PURE__*/ _jsx(Text, {
                variant: "xSmall",
                children: context.loc('Global_Loc_Marketplace_review', 'Marketplace review')
            })
        });
    };
    const getAriaLabel = (review)=>(rating, max)=>context.locParams('ReviewItem_AriaText', [
                `${rating}`,
                `${max}`,
                getFullName(review)
            ]);
    const isReviewEdited = (review)=>review.updated_at !== review.created_at;
    const reviewerName = getFullName(review);
    const isThirdPartyReview = review.source === Constants.StorefrontName.G2;
    return /*#__PURE__*/ _jsx("div", {
        ref: rootDivRef,
        className: contentStyles.reviewAndIsvReply,
        children: /*#__PURE__*/ _jsxs("div", {
            className: contentStyles.reviewItem,
            itemProp: "review",
            itemScope: true,
            itemType: "https://schema.org/Review",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "leftBar",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: contentStyles.reviewRating,
                            children: /*#__PURE__*/ _jsx(Rating, {
                                max: 5,
                                size: RatingSize.Small,
                                rating: review.rating,
                                styles: {
                                    ratingStarBack: {
                                        color: NeutralColors.gray100
                                    }
                                },
                                getAriaLabel: getAriaLabel(review),
                                readOnly: true
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: contentStyles.reviewDateWrapper,
                            children: /*#__PURE__*/ _jsxs(Stack, {
                                horizontal: true,
                                verticalAlign: "center",
                                tokens: {
                                    childrenGap: 8
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(Text, {
                                        block: true,
                                        className: mergeStyles(contentStyles.dateLabel, dateLabelClassName),
                                        children: getLocaleDate(review.updated_at, locale)
                                    }),
                                    isReviewEdited(review) && /*#__PURE__*/ _jsx(Text, {
                                        variant: "xSmall",
                                        className: contentStyles.editedLabel,
                                        children: context.loc('ReviewItem_EditedLabel')
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: contentStyles.authorDetailsContainer,
                            title: reviewerName,
                            itemProp: "author",
                            itemScope: true,
                            itemType: "https://schema.org/Person",
                            children: [
                                authorPersona ? /*#__PURE__*/ _jsx(Persona, {
                                    className: contentStyles.authorPersona,
                                    text: reviewerName,
                                    size: PersonaSize.size24
                                }) : reviewerName,
                                /*#__PURE__*/ _jsx("meta", {
                                    itemProp: "name",
                                    content: getFullName(review)
                                })
                            ]
                        }),
                        renderProviderSection(isThirdPartyReview)
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: contentStyles.reviewRightContent,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "rightBar",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: contentStyles.reviewContent,
                                children: [
                                    review.title ? /*#__PURE__*/ _jsx("h5", {
                                        className: contentStyles.reviewTitle,
                                        children: review.title
                                    }) : null,
                                    /*#__PURE__*/ _jsx(LongContent, {
                                        entityId: review.id,
                                        content: isThirdPartyReview ? ReviewQAContent({
                                            QAs: (_review_review_content = review.review_content) === null || _review_review_content === void 0 ? void 0 : _review_review_content.question_answer_content
                                        }) : review.content,
                                        clipHeight: contentClipHeight
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(AppReviewSocialContent, {
                            iconUrl: iconUrl,
                            isEmbed: isEmbed,
                            locale: locale,
                            onReportClick: ()=>showReportAbuseDialog(),
                            publisherName: publisher,
                            review: review,
                            isSignedIn: isSignedIn,
                            isMarkedAsHelpful: isMarkedAsHelpful,
                            onToggleMarkAsHelpful: onToggleMarkAsHelpful,
                            openSignInModal: openSignInModal
                        })
                    ]
                })
            ]
        })
    });
};
AppReviewItem.contextTypes = {
    loc: PropTypes.func,
    locParams: PropTypes.func,
    reportAbuseCallback: PropTypes.func
};
